import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
const _excluded = ["size", "style", "disabled", "className", "renderContent", "renderDragActiveContent"];
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { DROPZONE_SIZES, sizePropType } from './consts';
import DefaultActiveDragContent from './layout/DefaultActiveDragContent';
import DefaultContent from './layout/DefaultContent';
import DefaultContentSmall from './layout/DefaultContentSmall';
/**
 * Dropzone
 * A Dropzone is a component that provides an easy way to upload files by dragging
 * and dropping or by selecting the file’s location.
 * It supports multiple file uploads.
 */
const Dropzone = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      size = DROPZONE_SIZES.DEFAULT,
      style,
      disabled,
      className,
      renderContent,
      renderDragActiveContent = dragActiveContentProps => /*#__PURE__*/React.createElement(DefaultActiveDragContent, dragActiveContentProps)
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    isDragActive,
    getRootProps,
    getInputProps,
    open
  } = useDropzone(_objectSpread({
    disabled
  }, props));
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    ref: ref,
    className: className,
    role: "button",
    "data-testid": "dropzone"
  }, getRootProps(), {
    $_css: [_objectSpread(_objectSpread(_objectSpread({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      backgroundColor: theme.tokens.colors.background.primary.value,
      borderRadius: theme.borderRadius.small,
      border: "1px dashed ".concat(theme.tokens.colors.border.value),
      padding: "".concat(theme.spacing.L, "px ").concat(theme.spacing.M, "px"),
      overflow: 'hidden',
      cursor: isDragActive ? 'grabbing' : 'pointer',
      transition: theme.transitions.create(['border-color', 'background-color'], {
        duration: 150
      })
    }, !disabled && !isDragActive && {
      '&:hover': {
        borderColor: theme.tokens.colors.border.hover.dropzone.value
      }
    }), disabled && {
      opacity: 0.35,
      cursor: 'not-allowed'
    }), size === DROPZONE_SIZES.SMALL && {
      height: 64
    }), style]
  }), /*#__PURE__*/React.createElement("input", getInputProps()), /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css2: [{
      position: 'absolute',
      background: theme.tokens.colors.background.secondary.value,
      opacity: !isDragActive ? 0 : 1,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      transition: theme.transitions.create(['opacity'], {
        duration: 150
      }),
      zIndex: isDragActive ? 1 : -1
    }]
  }, isDragActive && renderDragActiveContent && renderDragActiveContent({
    size
  })), renderContent && renderContent(open), !renderContent && size === DROPZONE_SIZES.SMALL && /*#__PURE__*/React.createElement(DefaultContentSmall, {
    disabled: disabled,
    open: open
  }), !renderContent && size === DROPZONE_SIZES.DEFAULT && /*#__PURE__*/React.createElement(DefaultContent, {
    disabled: disabled,
    open: open
  }));
});
Dropzone.displayName = 'Dropzone';
Dropzone.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the dropzone is disabled */
  disabled: PropTypes.bool,
  /** Dropzone size */
  size: sizePropType,
  /** Callback to execute on file drop */
  onDrop: PropTypes.func,
  /** File types to accept */
  accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  /** Content to render */
  renderContent: PropTypes.func,
  /** Content to render when drag is active */
  renderDragActiveContent: PropTypes.func
};
export default Object.assign(Dropzone, {
  SIZES: DROPZONE_SIZES,
  Content: DefaultContent,
  ContentSmall: DefaultContentSmall
});
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledDiv2 = _styled2("div").withConfig({
  displayName: "Dropzone___StyledDiv2",
  componentId: "sc-oofm1m-0"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Dropzone.js.map